import {useEffect, useMemo} from 'react'

import './ui/app.css'
import useEngine from "./engine/useEngine.js";
import {useArmy} from "./data/army.js";
import {dispatch, useAppState} from "./state/store.js";
import {useLanguage} from "./state/language.js";
import {useTranslator} from "./state/useTranslator.js";
import {TProvider, useT} from "./ui/i18n.jsx";
import useMatchupView from "./state/useMatchupView.js";
import ClassicScreen from "./ui/ClassicScreen.jsx";
import MatchupScreen from "./ui/MatchupScreen.jsx";
import {AboutPage} from "./ui/AboutPage.jsx";
import {Menu, Toast} from "./ui/Menu.jsx";
import {SettingsPage} from "./ui/SettingsPage.jsx";
import {AboutDialog, HeaderButtons, SettingsDialog} from "./ui/DeskMenu.jsx";
import SavedRolls from "./ui/SavedRolls.jsx";
import {MoreIcon} from "./ui/icons.jsx";
import {MODES} from './state/modes.js';
import {useSearcher} from "./ui/picker/useTrooperSearch.js";
import useLayout, {usesDialogs} from "./ui/useLayout.js";

// The whole app is a function of the state document (src/state): the mode
// picks the screen, the screens read their slices, and the engine runs on the
// calculator params that the current mode derives.
function App() {
  const mode = useAppState((s) => s.mode);
  const classic = useAppState((s) => s.classic);
  const matchupMode = mode === MODES.matchup;
  const layout = useLayout();

  const lang = useLanguage();
  const {t: base, ready} = useTranslator(lang);
  useEffect(() => { document.documentElement.lang = base.lang; }, [base.lang]);
  const {army, error: armyError, retry: retryArmy} = useArmy(matchupMode, lang);
  // The army's own translations of the tokens the rules read (+1B, BS Mode),
  // once the army is in the same language as the copy.
  const t = useMemo(() => base.withTerms(army?.lang === base.lang ? army.terms : null), [base, army]);
  const view = useMatchupView(army, t);

  // Shock is Matchup-only (it depends on the target's VITA); the classic
  // calculator has no input for it, so it never counts there. An incomplete
  // matchup has no params and calculates nothing.
  const engineParams = useMemo(
    () => (matchupMode ? view.params ?? null : {...classic, shockA: false, shockB: false}),
    [matchupMode, view.params, classic],
  );
  const engine = useEngine(engineParams);
  // The roll on screen, for the desktop Settings' headline previews.
  const results = useMemo(() => ({
    result: matchupMode ? (view.complete ? engine.result : null) : engine.result,
    targets: matchupMode ? view.targets : null,
    params: engineParams,
  }), [matchupMode, view.complete, view.targets, engine.result, engineParams]);
  const dialogs = usesDialogs(layout);

  // A first visit in another language waits for its copy (a small chunk)
  // rather than flash English.
  if (!ready) return null;
  return (
    <TProvider value={t}>
    <div className={`app ${layout}`}>
      <header className="app-header">
        <span className="wordmark">INFINITY THE CALCULATOR{!matchupMode && layout === 'phone' && <small> · {t('mode.classic').toLocaleUpperCase(t.lang)}</small>}</span>
        {layout !== 'phone' && (
          <span className="mode-switch" role="group" aria-label={t('menu.calculator')}>
            {/** @type {[string, string][]} */ ([[MODES.matchup, t('mode.matchup')], [MODES.basic, t('mode.classic')]]).map(([m, name]) => (
              <button type="button" key={m} className={mode === m ? 'on' : ''} aria-pressed={mode === m}
                onClick={() => dispatch({type: 'setMode', mode: m})}>{name}</button>
            ))}
          </span>
        )}
        {dialogs
          ? <HeaderButtons />
          : <button type="button" className="icon-btn" aria-label={t('menu.menu')} onClick={() => dispatch({type: 'openOverlay', overlay: 'menu'})}><MoreIcon /></button>}
      </header>
      {matchupMode
        ? <MatchupScreen army={army} armyError={armyError} retryArmy={retryArmy} view={view} engine={engine} />
        : <ClassicScreen params={classic} engine={engine} />}
      <Overlays dialogs={dialogs} results={results} />
      <Toast />
    </div>
    </TProvider>
  )
}

// The menu and its pages. Their own component, so opening or closing one
// re-renders only this, not the screen behind. `dialogs`: wide layouts, where
// the header buttons open them as dialogs (DeskMenu.jsx).
/** @param {{dialogs: boolean, results: {result: import('./types.js').EngineResult | null, targets: import('./types.js').MatchupCore['targets'], params: import('./types.js').Params | null}}} props */
function Overlays({dialogs, results}) {
  const overlay = useAppState((s) => s.ui.overlay);
  // Settings lists the factions; the search index has them without the army data.
  const {searcher} = useSearcher(overlay === 'settings', useLanguage());
  const factions = searcher?.factions ?? [];
  if (overlay === 'menu') return <Menu />;
  if (overlay === 'saved') return <SavedRolls />;
  if (overlay === 'settings') return dialogs ? <SettingsDialog factions={factions} results={results} /> : <SettingsPage factions={factions} />;
  if (overlay === 'about') return dialogs ? <AboutDialog /> : <AboutPage />;
  return null;
}

export default App
